import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';
import { useActiveLocationId } from '@/lib/locationContext';

/**
 * The referral programme AS THE PRO'S STUDIO RUNS IT — the gate on the whole
 * Refer section (Peter, 2026-08-27: "Refer in the Pro Hub is not visible
 * unless the location has referral turned on").
 *
 * Reads the same store the public page reads (public.location_kb_values,
 * ns='lease' referral keys) through the pro's own session — the RLS read
 * policy admits a location the pro belongs to, probed as QA Pro's real
 * identity before this was written.
 *
 * ABSENT MEANS ON (LIB-13): My Studio shows the toggle ON when the key has
 * never been stored, and the public page reads the same absence as on. A
 * third reader must not invent a third opinion — an explicit 'no' is off,
 * silence is on. All three readers agree or an owner sees three behaviours.
 */
/** LIB-13, verbatim: never-set means ON, an explicit 'no' is off. Exported so
 *  the semantics are unit-testable against the other two readers' contract. */
export function referralEnabledFrom(v: string | undefined | null): boolean {
  const t = String(v ?? '').trim().toLowerCase();
  return t === '' ? true : t === 'yes';
}

export interface ReferralProgramme {
  loading: boolean;
  enabled: boolean;
  bonus: string | null;
  terms: string | null;
}

export function useReferralProgramme(): ReferralProgramme {
  const { activeLocationId } = useActiveLocationId();
  const { data, isLoading } = useQuery({
    queryKey: ['referral-programme', activeLocationId],
    enabled: !!activeLocationId,
    staleTime: 60_000,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/kb?set=referral — the same three keys, any namespace.
      const { data: rows } = isCell()
        ? { data: (await cellGet<{ rows: Array<{ key: string; value: string | null }> }>(`/v1/pro-hub/locations/${encodeURIComponent(activeLocationId!)}/kb`, { set: 'referral' })).data?.rows ?? null }
        : await supabase
        .from('location_kb_values')
        .select('key, value')
        .eq('location_id', activeLocationId!)
        .in('key', ['referral_enabled', 'referral_bonus', 'referral_terms']);
      const val = (k: string) => rows?.find((r) => r.key === k)?.value ?? undefined;
      return {
        enabled: referralEnabledFrom(val('referral_enabled')),
        bonus: val('referral_bonus')?.trim() || null,
        terms: val('referral_terms')?.trim() || null,
      };
    },
  });
  return {
    // While loading (or with no location yet), the section stays HIDDEN — a
    // nav item that appears is fine; one that vanishes after a beat reads as
    // the app taking something away.
    loading: isLoading,
    enabled: data?.enabled ?? false,
    bonus: data?.bonus ?? null,
    terms: data?.terms ?? null,
  };
}
